import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import * as Yup from 'yup';
import { getIn, type FieldMetaProps } from 'formik';

import { cn } from '@/lib/utils';
import { Tone } from '@gokrew/models/ui.types';
import {
  LeaveRequestStatus,
  type LeaveRequestItem,
  type Meridiem,
} from '@gokrew/models/leaves';
import { EmployeeProbationStatus } from '@gokrew/models/employee';
import type { ComboBoxOption } from '@/components/ComboBox';
import type { YearOption } from '@gokrew/models/leave-management';
import i18n from '../i18n';
import {
  translateApiErrorMessage,
  type ApiErrorTranslator,
} from './apiErrorTranslations';

export { formatPhone, formatPhoneNumber, phoneHref } from './phone';

export { translateApiErrorMessage, type ApiErrorTranslator };

/**
 * Built-in date format presets
 */
const dateFormatMap = {
  short: 'M/D/YY', // 9/9/25
  medium: 'MMM D, YYYY', // Sep 9, 2025
  long: 'MMMM D, YYYY', // September 9, 2025
  full: 'dddd, MMMM D, YYYY', // Tuesday, September 9, 2025
  iso: 'YYYY-MM-DD', // 2025-09-09
  mediumTime: 'MMM D, YYYY h:mm A', // Sep 9, 2025 2:30 PM
  monthDay: 'MMM D', // Sep 9
  monthDayTime: 'MMM D, h:mm A', // Sep 9, 2:30 PM
} as const;

type DateFormatPreset = keyof typeof dateFormatMap;

const localeDateFormatOverrides: Record<
  string,
  Partial<Record<DateFormatPreset, string>>
> = {
  fr: {
    short: 'DD/MM/YY', // 09/09/25
    full: 'dddd D MMMM YYYY',
    medium: 'D MMM YYYY', // 9 sept. 2025
    long: 'D MMMM YYYY',
    mediumTime: 'D MMM YYYY HH:mm', // 9 sept. 2025 14:30
    monthDay: 'D MMM', // 9 sept.
    monthDayTime: 'D MMM, HH:mm', // 9 sept., 14:30
  },
};

/** The overrides for a language, whatever its region (`fr-CA` reads as `fr`). */
const localeOverrides = (locale: string) =>
  localeDateFormatOverrides[locale] ??
  localeDateFormatOverrides[locale.split('-')[0].toLowerCase()];

/**
 * The app language, for a date formatted without an explicit locale, so a
 * preset reads "11 oct. 2026" on a French screen even when the caller forgot
 * to pass `locale` (film bug 2026-10-11). English when i18next is not set up.
 */
const appLanguage = (): string => i18n.language || 'en';

type DateValue = Date | undefined | string | number | null;
type PresetConfig = { format?: DateFormatPreset; locale?: string };
type CustomConfig = { format?: string; locale?: string };

function isPresetConfig(format: unknown): format is DateFormatPreset {
  return typeof format === 'string' && format in dateFormatMap;
}

/**
 * French writes the first day of a month "1er" ("1er janv. 2027", "chaque
 * 1er janvier"), every other day as a plain number. Applies to a day token
 * `D` written before a month name; numeric formats are left alone.
 */
function withFrenchFirstOfMonth(
  format: string,
  day: number,
  locale: string
): string {
  if (day !== 1 || !locale.toLowerCase().startsWith('fr')) return format;
  return format.replace(/(^|[^D[\]])D(?=\s+M)/g, '$1[1er]');
}

// ── Overloads (for better IntelliSense) ──────────────────────────────────────
/** Format a date using default preset (medium). */
export function formatDate(value: DateValue): string;
/** Format a date using one of the built-in presets. */
export function formatDate(value: DateValue, config: PresetConfig): string;
/** Format a date using a custom format string. */
export function formatDate(value: DateValue, config: CustomConfig): string;
// ── Implementation ──────────────────────────────────────────────────────────
/**
 * Formats a date using preset patterns (`short`, `medium`, `long`, `full`,
 * `iso`, `mediumTime`, `monthDay`, `monthDayTime`) or a custom Day.js format
 * string. Prefer a preset for anything a person reads: it puts the day and
 * month in the language's order ("Oct 11, 2026" / "11 oct. 2026"), which a
 * custom format such as `MMM D, YYYY` gets wrong in French.
 *
 * @param value - The date to format. Can be a Date, timestamp, ISO string, or null/undefined.
 * @param config - Optional configuration for formatting.
 * @param config.format - Either one of the preset keys (see above)
 *                        or a custom Day.js format string (e.g. `"DD/MM/YYYY"`).
 * @param config.locale - Locale string passed to Day.js (e.g. `"en"`, `"fr"`, `"en-ca"`).
 *                        Defaults to the current app language.
 *
 * @returns The formatted date string, or an empty string if input is invalid.
 */
export function formatDate(
  value: DateValue,
  config: PresetConfig | CustomConfig = {}
): string {
  if (!value) return '';
  const date = dayjs(value).clone();
  if (!date.isValid()) {
    console.warn('Invalid date passed to formatDate:', value);
    return '';
  }
  const rawFormat = config.format ?? 'medium';
  const locale = config.locale || appLanguage();
  const localized = date.locale(locale);
  const format = isPresetConfig(rawFormat)
    ? (localeOverrides(locale)?.[rawFormat] ?? dateFormatMap[rawFormat])
    : rawFormat;
  return localized.format(
    withFrenchFirstOfMonth(format, localized.date(), locale)
  );
}

/**
 * Concatenates the firstName and lastName properties of the given object
 * into a single string, with a space in between.
 *
 * @param value the object with firstName and lastName properties
 * @returns the concatenated string
 */
export function getNameFromObj(
  value?: Partial<Record<'firstName' | 'lastName', string>>
) {
  if (!value) return '';
  return `${value?.firstName || ''} ${value?.lastName || ''}`.trim();
}

/**
 * Formats the street part of an address from addressLine_1 and
 * addressLine_2, joined with a comma. Either line may be missing, since every
 * part of an address is optional; with neither, returns an empty string.
 */
export function getAddressFromObj(
  value?: Partial<
    Record<'addressLine_1' | 'addressLine_2', string | null>
  > | null
) {
  if (!value) return '';
  return [value.addressLine_1, value.addressLine_2]
    .filter((line): line is string => !!line?.trim())
    .join(', ');
}

/**
 * Generates an array of options for a dropdown select based on the given enum value.
 * The resulting options will have the `label` and `value` properties.
 * The `label` property will be generated by concatenating the `labelPrefix` with the enum value,
 * or by using the `t` function to translate the labels.
 *
 * @param enumVal the enum value to generate options from
 * @param labelPrefix the prefix to use when generating labels
 * @param t a function to translate labels
 * @returns an array of options
 */
export function generateEnumOptions<T extends Record<string, string>>(
  enumVal: T,
  labelPrefix?: string,
  t?: (key: string) => string
) {
  return Object.values(enumVal).map((value) => ({
    label: labelPrefix && t ? t(`${labelPrefix}.${value}`) : value,
    value,
  }));
}

const ETC_GMT_OFFSET_REGEX = /^Etc\/GMT([+-])(\d{1,2})$/;

/**
 * Converts a timezone identifier to a user-friendly label.
 */
export function getTimezoneLabel(
  tz: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  tOptions?: Record<string, unknown>
): string {
  const match = tz.match(ETC_GMT_OFFSET_REGEX);
  if (match) {
    const hours = (match[1] === '+' ? -1 : 1) * Number(match[2]);
    const offset = `${hours >= 0 ? '+' : '-'}${String(Math.abs(hours)).padStart(2, '0')}:00`;
    return `(UTC${offset}) GMT${offset}`;
  }
  return t(`allowed_timezones.${tz}`, { defaultValue: tz, ...tOptions });
}

/**
 * Translates a half-day leave session (Meridiem AM/PM) into its display label
 * ("First Half"/"Second Half"). Falls back to the raw session value for any
 * unrecognized value (e.g. legacy data) instead of leaking the i18n key.
 */
export function getHalfDaySessionLabel(
  session: Meridiem | string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
) {
  if (!session) return '';
  return t(`half_day_session.${session}`, { defaultValue: session });
}

/**
 * Formats an array of objects into an array of options for a combobox select.
 * The resulting options will have the `label` and `value` properties.
 * The `label` property will be generated by using the `labelKeyFn` function,
 * or by accessing the `labelKey` property of the objects in the given array.
 * The `value` property will be generated by accessing the `valueKey` property of the objects in the given array.
 * @param data the array of objects to format
 * @param labelKeyFn a function to generate the label of an option
 * @param labelKey the key of the property to access for generating the label of an option
 * @param valueKey the key of the property to access for generating the value of an option
 * @returns an array of options
 */
export function formatComboboxOptions<T>({
  data = [] as Array<T>,
  labelKeyFn,
  valKeyFn,
  disabledOptionFn,
  visibilityOptionFn,
  labelKey = 'name' as keyof T,
  valueKey = 'id' as keyof T,
}: {
  data: Array<T> | undefined;
  labelKey?: keyof T;
  labelKeyFn?: (d: T) => string;
  valueKey?: keyof T;
  valKeyFn?: (d: T) => string;
  disabledOptionFn?: (d: T) => boolean;
  visibilityOptionFn?: (d: T) => boolean;
}): ComboBoxOption[] {
  return data.map((option) => ({
    value: valKeyFn ? valKeyFn(option) : (option[valueKey] as string),
    label: labelKeyFn ? labelKeyFn(option) : (option[labelKey] as string),
    ...(disabledOptionFn && { disabled: disabledOptionFn(option) }),
    ...(visibilityOptionFn && { visibility: visibilityOptionFn(option) }),
  }));
}

/**
 * Replaces all underscore characters in the given string with spaces.
 * Returns an empty string if the input is null or undefined.
 * @param text the string to replace underscore characters in
 * @returns the string with all underscore characters replaced with spaces
 */
export const replaceUnderscoreWithSpace = (text: {
  toString: () => string;
}) => {
  if (!text) return '';
  return text.toString().replace(/_/g, ' ');
};

/**
 * Checks if a field in a Yup schema is required.
 * Returns false if the schema is null or the field does not exist.
 * @param schema the Yup schema to check
 * @param path the path to the field to check
 * @returns true if the field is required, false otherwise
 */
export function isFieldRequired(
  schema: Yup.AnySchema | null,
  path: string,
  values?: Record<string, unknown>
): boolean {
  try {
    if (!schema) return false;

    let fieldSchema = Yup.reach(schema, path);

    if (values) {
      // A nested field (`location.provinceId`) resolves its `when` against
      // its own object, not the form root.
      const parentPath = path.replace(/\.?[^.[\]]+\]?$/, '');
      const resolved = (fieldSchema as Yup.AnySchema).resolve({
        value: getIn(values, path),
        parent: parentPath ? getIn(values, parentPath) : values,
        context: {},
      });
      fieldSchema = resolved;
    }

    const desc = fieldSchema.describe() as Yup.SchemaFieldDescription & {
      tests: { name: string }[];
      optional: boolean;
    };

    return (
      desc.tests?.some((t) => t.name === 'required') || desc.optional === false
    );
  } catch {
    return false;
  }
}

/**
 * Returns a string of CSS classes that should be applied to a form field.
 * The returned classes will include the provided className, and will also include
 * classes for disabled and error states if the corresponding props are truthy.
 */
export function getFormFieldClasses<T>({
  className,
  disabled,
  meta,
}: {
  className?: string;
  disabled?: boolean;
  meta?: FieldMetaProps<T>;
}) {
  const showError = meta?.touched && !!meta.error;
  return cn(
    className,
    disabled &&
      'bg-gray-100 border-gray-300 text-gray-400 placeholder:text-gray-400 cursor-not-allowed',
    showError && 'border-red-500 focus:ring-red-500 focus:border-red-500'
  );
}

/**
 * Maps a boolean status value to a Tone enum value.
 * Returns Tone.SUCCESS if the status is true, and Tone.ERROR if the status is false.
 */
export function getToneFromBool(status: boolean) {
  if (status) return Tone.SUCCESS;
  return Tone.ERROR;
}

export const leaveStatusToneMap: Record<LeaveRequestStatus, Tone> = {
  [LeaveRequestStatus.APPROVED]: Tone.SUCCESS,
  [LeaveRequestStatus.AUTO_APPROVED]: Tone.SUCCESS,
  [LeaveRequestStatus.CANCELLED]: Tone.SECONDARY,
  [LeaveRequestStatus.PENDING]: Tone.WARNING,
  [LeaveRequestStatus.REJECTED]: Tone.ERROR,
};

export const probationStatusToneMap: Record<EmployeeProbationStatus, Tone> = {
  [EmployeeProbationStatus.Active]: Tone.PRIMARY,
  [EmployeeProbationStatus.Completed]: Tone.SUCCESS,
  [EmployeeProbationStatus.Extended]: Tone.ERROR,
};

export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const twoDecimals = (n: number) => Number.isInteger(n * 100);

interface FormatConfig {
  weekConfig?: Intl.DateTimeFormatOptions;
  dateConfig?: PresetConfig | CustomConfig;
  getSessionLabel?: (session: Meridiem | null) => string;
  omitSessionLabel?: boolean;
}

export function formatLeaveDuration<TReq extends LeaveRequestItem>(
  req: TReq,
  config: FormatConfig = {}
) {
  const isSameDay = req.startDate === req.endDate;
  const { getSessionLabel, omitSessionLabel } = config;
  let { weekConfig, dateConfig } = config;
  weekConfig = weekConfig ?? { weekday: 'long' };
  dateConfig = dateConfig ?? { format: 'medium' };
  const locale = dateConfig.locale || appLanguage();

  const startDate = formatDate(req.startDate, dateConfig);
  const startDateWeekDay = getWeekDay(req.startDate, weekConfig, locale);
  const endDate = req.endDate ? formatDate(req.endDate, dateConfig) : null;
  const endDateWeekDay = req.endDate
    ? getWeekDay(req.endDate, weekConfig, locale)
    : null;

  // "Fri, 28 September 2026" — weekday and date on a single line.
  const withWeekDay = (date: string, weekDay: string | null) =>
    weekDay ? `${weekDay}, ${date}` : date;
  const startLine = withWeekDay(startDate, startDateWeekDay);
  const endLine = endDate ? withWeekDay(endDate, endDateWeekDay) : null;

  const baseRes = {
    startDate,
    endDate,
    startDateWeekDay,
    endDateWeekDay,
    timeRange: undefined as string | undefined,
    sessionLabel: undefined as string | undefined,
  };

  // ── Hourly ───────────────────────────────
  if (req.startTime && req.endTime) {
    const [sh, sm] = req.startTime.split(':').map(Number);
    const [eh, em] = req.endTime.split(':').map(Number);
    const startFormatted = formatTime(new Date(2000, 0, 1, sh, sm), locale);
    const endFormatted = formatTime(new Date(2000, 0, 1, eh, em), locale);
    return {
      ...baseRes,
      dateLine: startDate,
      weekDayDateLine: startLine,
      timeRange: `${startFormatted}–${endFormatted}`,
      weekDayLine: getWeekDay(req.startDate, weekConfig, locale),
    };
  }
  // ── Half day ─────────────────────────────
  if (req.days === 0.5) {
    const sessionLabel = getSessionLabel
      ? getSessionLabel(req.startSession)
      : (req.startSession ?? undefined);
    const suffix = omitSessionLabel ? '' : ` (${sessionLabel})`;
    return {
      ...baseRes,
      sessionLabel,
      dateLine: `${startDate}${suffix}`,
      weekDayDateLine: `${startLine}${suffix}`,
      weekDayLine: getWeekDay(req.startDate, weekConfig, locale),
    };
  }
  // ── Single full day ──────────────────────
  if (isSameDay) {
    return {
      ...baseRes,
      dateLine: startDate,
      weekDayDateLine: startLine,
      weekDayLine: getWeekDay(req.startDate, weekConfig, locale),
    };
  }
  // ── Multi day ────────────────────────────
  return {
    ...baseRes,
    dateLine: endDate ? `${startDate} - ${endDate}` : startDate,
    weekDayDateLine: endLine ? `${startLine} - ${endLine}` : startLine,
    weekDayLine: endDateWeekDay
      ? `${startDateWeekDay} – ${endDateWeekDay}`
      : startDateWeekDay,
  };
}

export const getWeekDay = (
  date: string,
  config: Intl.DateTimeFormatOptions = { weekday: 'long' },
  locale = 'en-US'
) => {
  if (!date) return '';

  const iso = date.split('T')[0];
  const [year, month, day] = iso.split('-').map(Number);

  const safeDate = new Date(year, month - 1, day);

  return safeDate.toLocaleDateString(locale, config);
};

/**
 * Generates an array of year options based on the current year
 * and the specified number of past and future years.
 *
 * @param The number of years to include in the past (default to 5)
 * @param The number of years to include in the future (default to 5)
 * @returns An array of year options
 */
export function getYearOptions(
  offsetPastYears = 5,
  offsetFutureYears = 5
): ComboBoxOption<number>[] {
  const today = new Date();
  const currentYear = today.getFullYear();
  return Array.from(
    { length: offsetPastYears + 1 + offsetFutureYears }, // +1 makes sure that the current year is added in the option as well
    (_, i) => {
      const year = currentYear - (offsetPastYears - i);
      return { label: year.toString(), value: year };
    }
  );
}

/**
 * Returns today's date with the time zeroed out (midnight local time),
 * so it can be compared against or used as a calendar `minDate` without
 * the current time of day affecting the comparison.
 */
export function getStartOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Formats a given number to have a maximum of two decimal places.
 * @param value The number to format
 * @returns The formatted number with a maximum of two decimal places
 */
export const formatMax2Decimals = (value: number): number =>
  Math.round(value * 100) / 100;
/**
 * Clamps a given value to be within a specified range.
 * @param value The value to clamp
 * @param min The minimum value of the range (default to 0)
 * @param max The maximum value of the range (default to 100)
 * @returns The clamped value
 */
export const clamp = (value: number, min = 0, max = 100) =>
  Math.min(Math.max(value, min), max);

export function getUserInitials({
  firstName,
  lastName,
}: {
  firstName?: string;
  lastName?: string;
}) {
  const firstInitial = firstName?.trim()?.[0] || '';
  const lastInitial = lastName?.trim()?.[0] || '';

  const initials = `${firstInitial}${lastInitial}`;

  return initials ? initials.toUpperCase() : '';
}

/** "First Last" for a user, or "-" when both names are empty. */
export function getFullNameFromUser(user?: {
  firstName?: string;
  lastName?: string;
}) {
  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  return name || '-';
}

/**
 * Derives a 1-2 letter avatar badge from a free-form display name (e.g. a
 * workflow, announcement, or list title — not a person's firstName/lastName,
 * see getUserInitials for that). Strips punctuation/symbols/emoji from each
 * word before taking its first character, so leading special characters
 * never end up as an initial.
 */
export function getInitialsFromName(name: string): string {
  if (!name) return '';

  const words = name
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);

  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}
export function generateYearOptions(
  centerYear: number,
  pastYears = 5,
  futureYears = 5
): YearOption[] {
  return Array.from({ length: pastYears + futureYears + 1 }, (_, i) => {
    const year = centerYear - pastYears + i;
    return {
      label: String(year),
      value: year,
    };
  });
}

export function snakeToCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
}

export function getFileNameFromURL(path: string): string {
  const fileName = path.split('/').pop() || '';
  const index = fileName.indexOf('_');

  return index !== -1 ? fileName.substring(index + 1) : fileName;
}

/**
 * A stored file's name as a URL carries it: signed URLs append a query string,
 * and the path is percent-encoded.
 *
 * A lone `%` in a real file name makes `decodeURIComponent` throw, and these
 * names are read during render — an undecodable path falls back to its raw
 * form rather than taking the whole dialog down with it.
 */
export function getDisplayFileName(url: string): string {
  const path = url.split('?')[0];
  let decoded = path;

  try {
    decoded = decodeURIComponent(path);
  } catch {
    // Not valid percent-encoding; the raw path still reads as a name.
  }

  return getFileNameFromURL(decoded) || url;
}

export function getFileExtension(nameOrUrl?: string): string {
  if (!nameOrUrl) return '';
  const clean = nameOrUrl.split('?')[0];
  const match = clean.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : '';
}

export const formatFileSize = (bytes?: string | number): string => {
  const num = typeof bytes === 'string' ? Number(bytes) : bytes;

  if (num === undefined || isNaN(num) || num <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];

  const exponent = Math.min(Math.floor(Math.log2(num) / 10), units.length - 1);

  const value = num / 1024 ** exponent;

  return `${Number(value.toFixed(2))} ${units[exponent]}`;
};

export const dayLabelMap: Record<string, string> = {
  Monday: 'monday',
  Tuesday: 'tuesday',
  Wednesday: 'wednesday',
  Thursday: 'thursday',
  Friday: 'friday',
  Saturday: 'saturday',
  Sunday: 'sunday',
};

export const maskAccountNumber = (account: string) =>
  account.slice(-4).padStart(account.length, '*');

/** Trims a "HH:mm:ss" time string to "HH:mm". */
export const formatTimeHHMM = (time: string): string => time.slice(0, 5);

/**
 * Formats a date's time portion using the locale's conventions
 * (e.g. 12-hour with AM/PM for English, 24-hour for French).
 */
export const formatTime = (value: DateValue, locale = 'en'): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractMessage = (error: any): string => {
  const raw = error.response?.data?.message;
  if (Array.isArray(raw)) return raw[0];
  if (typeof raw === 'string') return raw;
  return 'No response';
};

/**
 * Like extractMessage, but takes a caller-supplied (typically translated)
 * fallback instead of the hardcoded 'No response' sentinel — for call sites
 * that want their own copy when the response carries no message (e.g. a
 * network failure), rather than the generic Sentry-facing string.
 */
export const getApiErrorMessage = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  fallback: string
): string => {
  const raw = error?.response?.data?.message;
  if (Array.isArray(raw) && typeof raw[0] === 'string') return raw[0];
  if (typeof raw === 'string') return raw;
  return fallback;
};

export const getUserErrorMessage = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  i18n?: ApiErrorTranslator
): string => {
  if (!error.response) return 'Something went wrong';
  const message = extractMessage(error);
  const errorCode = error.response?.data?.errorCode;
  return translateApiErrorMessage(message, i18n, errorCode);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getSentryErrorMessage = (error: any, prefix?: string): string => {
  const status: number | undefined = error.response?.status;
  const url: string = error.config?.url ?? 'N/A';
  const message = extractMessage(error);
  const prefixPart = prefix ? `${prefix} | ` : '';

  if (!error.response)
    return `${prefixPart}Network Error | code: ${error.code ?? 'N/A'} | url: ${url}`;

  const statusStr = status ? `status: ${status}` : '';
  const typePart = status
    ? status >= 500
      ? 'Backend Error'
      : 'Bad Request'
    : 'Error';
  return `${prefixPart}${typePart}${statusStr ? ` | ${statusStr}` : ''} | ${message} | url: ${url}`;
};
